import React from 'react';
import { IconButton, Box, Grid, BoxProps, GridProps } from '@mui/material';
import { ArrowNext, ArrowPrev } from 'components/SVG';
import SkeletonCard from 'sharedComponents/Skeleton/SkeletonCard';
import { useResize } from 'hooks/common';

import './sliding-card.css';

const getSkeletons = (
  skeletonCount: number,
  SkeletonComponent?: React.ElementType
) => {
  return Array.from({ length: skeletonCount }).map((_, index) => (
    <Grid item xs={12} key={index}>
      {SkeletonComponent ? <SkeletonComponent /> : <SkeletonCard />}
    </Grid>
  ));
};

type SparkCarouselPropTypes = {
  isLoading?: boolean;
  isFetchingNextPage?: boolean;
  children: React.ReactNode | React.ReactNode[];
  childContainerStyles?: BoxProps['sx'];
  parentContainerStyles?: GridProps['sx'];
  onLastElementView: () => void;
  skeletonCount?: number;
  childrenGridProps?: GridProps;
  skeletonComponent?: React.ElementType;
  renderArrowButtons?: boolean;
};

function SparkCarousel({
  isLoading = false,
  isFetchingNextPage = false,
  children,
  childContainerStyles = {},
  parentContainerStyles = {},
  onLastElementView,
  skeletonCount = 10,
  childrenGridProps,
  skeletonComponent: SkeletonComponent,
  renderArrowButtons = true,
}: SparkCarouselPropTypes) {
  const { isMobile } = useResize();
  const containerRef = React.useRef<HTMLDivElement>(null);
  const firstElementRef = React.useRef<HTMLDivElement>(null);
  const lastElementRef = React.useRef<HTMLDivElement>(null);

  const [isFirstVisible, setIsFirstVisible] = React.useState(true);
  const [isLastVisible, setIsLastVisible] = React.useState(false);

  const scroll = (scrollOffset: number) => {
    if (containerRef.current) {
      containerRef.current.scrollLeft += scrollOffset;
    }
  };
  React.useEffect(() => {
    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (entry.target === firstElementRef.current) {
          setIsFirstVisible(entry.isIntersecting);
        }
        if (entry.target === lastElementRef.current) {
          setIsLastVisible(entry.isIntersecting);
          if (entry.isIntersecting) {
            onLastElementView();
          }
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, {
      root: containerRef.current,
      threshold: 1,
    });

    if (firstElementRef.current) {
      observer.observe(firstElementRef.current);
    }
    if (lastElementRef.current) {
      observer.observe(lastElementRef.current);
    }

    return () => {
      if (firstElementRef.current) {
        observer.unobserve(firstElementRef.current);
      }
      if (lastElementRef.current) {
        observer.unobserve(lastElementRef.current);
      }
    };
  }, [onLastElementView]);

  const shouldRenderNextButton = React.useMemo(() => {
    if (
      !renderArrowButtons ||
      !children ||
      (Array.isArray(children) && !children.length)
    ) {
      return false;
    }
    return !isLastVisible && !isMobile;
  }, [children, isLastVisible, isMobile, renderArrowButtons]);

  const shouldRenderPrevButton = React.useMemo(() => {
    if (
      !renderArrowButtons ||
      !children ||
      (Array.isArray(children) && !children.length)
    ) {
      return false;
    }
    return !isFirstVisible && !isMobile;
  }, [children, isFirstVisible, isMobile, renderArrowButtons]);

  const conditionalFadeClass = React.useMemo(() => {
    if (
      (isFirstVisible && isLastVisible) ||
      (Array.isArray(children) && children.length === 1)
    ) {
      return '';
    }
    if (!isFirstVisible && !isLastVisible) {
      return 'both-fades';
    }
    if (!isFirstVisible) {
      return 'left-fade';
    }
    if (!isLastVisible) {
      return 'right-fade';
    }
    return '';
  }, [children, isFirstVisible, isLastVisible]);

  const childrenContent = React.useMemo(() => {
    if (isLoading) {
      return <>{getSkeletons(skeletonCount, SkeletonComponent)}</>;
    }

    if (Array.isArray(children)) {
      let childrenToRender: React.ReactNode[] = children;
      if (isFetchingNextPage) {
        childrenToRender = [
          ...children,
          ...getSkeletons(skeletonCount, SkeletonComponent),
        ];
      }

      return (
        <>
          {childrenToRender?.map((child, index) => (
            <Box
              width={350}
              minWidth={350}
              maxWidth={350}
              height={250}
              minHeight={250}
              key={index}
              ref={
                index === 0
                  ? firstElementRef
                  : index === children.length - 1
                    ? lastElementRef
                    : null
              }
              sx={childContainerStyles}
            >
              {child}
            </Box>
          ))}
        </>
      );
    }
    return children;
  }, [childContainerStyles, children, isFetchingNextPage, isLoading]);

  return (
    <Grid
      position='relative'
      item
      container
      sx={{
        ...parentContainerStyles,
      }}
    >
      <Grid
        item
        className={`sliding-card-container  ${
          !isLoading ? conditionalFadeClass : ''
        }`}
        ref={containerRef}
        sx={{ scrollBehavior: 'smooth' }}
      >
        <Grid
          container
          item
          xs={12}
          gap={2}
          flexWrap='nowrap'
          padding={2}
          {...childrenGridProps}
        >
          {childrenContent}
        </Grid>
      </Grid>
      {shouldRenderNextButton && (
        <Box
          height='100%'
          position='absolute'
          right={0}
          display='flex'
          justifyContent='center'
          alignItems='center'
          sx={{
            zIndex: 10,
          }}
        >
          <IconButton onClick={() => scroll(432)}>
            <ArrowNext />
          </IconButton>
        </Box>
      )}
      {shouldRenderPrevButton && (
        <Box
          height='100%'
          position='absolute'
          left={0}
          display='flex'
          justifyContent='center'
          alignItems='center'
          sx={{
            zIndex: 10,
          }}
        >
          <IconButton onClick={() => scroll(-432)}>
            <ArrowPrev />
          </IconButton>
        </Box>
      )}
    </Grid>
  );
}

export default SparkCarousel;
